<template>
    <div>
        <Loader v-if="loading" />
        <div v-else>
            <div class="container-fluid mt-45">
                <div class="row">
                    <div class="col d-flex flex-column flex-md-row justify-content-between align-items-start">
                        <div>
                            <Breadcrumbs />

                            <PatientSearchbar 
                                v-on:updateResults="updateResults"
                                v-on:clear="clear"
                            />
                        </div>

                        <div class="mt-4 mt-md-0">
                            <router-link :to="{name: 'CreatePatient'}" class="btn btn-success btn-lg btn-assign-test btn-block-mobile lg-shadow mr-2">Create Patient</router-link>
                        </div>
                    </div>
                </div>

                <div v-if="results.results && !results.results.length" class="row mt-4">
                    <div class="col-md-6">
                        <div class="alert alert-info">
                            There are no patients to show
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div class="row mt-4">
                        <div class="col">
                            <p class="h4">Results <small class="font-italic text-sm">{{ `Showing ${showingComputed} of ${results.totalCount}` }}</small></p>
                        </div>
                    </div>

                    <div class="row mt-2">
                        <div class="col">
                            <b-table 
                                ref="table"
                                id="table"
                                :sort-by.sync="sortBySync" 
                                :sort-desc.sync="sortDesc" 
                                @sort-changed="sortingChanged" 
                                :no-local-sorting="true"
                                :busy.sync="isBusy"
                                class="custom-table bg-white" 
                                responsive
                                no-sort-reset
                                :items="results.results"
                                :fields="filteredPatientsFields"
                            >
                                <template #cell(fullName)="data">
                                    <router-link :to="{name: 'ViewPatient', params: {id : data.item.id}}" class="underlined font-weight-bold text-capitalize">{{ `${formatTitle(data.item.title)} ${data.item.firstName} ${data.item.surname}` }}</router-link>
                                </template>

                                <template #cell(previousTests)="data">
                                    {{ data.item.testIds.length }}
                                </template>

                                <template #cell(action)="data" class="d-flex flex-shrink-0 flex-grow-1">
                                    <button v-show="!facilitiesAllDisabled" @click="assignTestKitToPatient(data.item)" class="btn btn-success sm-shadow text-nowrap">Assign a Test Kit</button>
                                </template>
                            </b-table>

                            <div class="mt-4 d-flex flex-column flex-md-row align-items-center justify-content-end">
                                <div class="form-group d-flex align-items-center mb-4 mb-md-0">
                                    <label class="flex-shrink-0 mr-2">Items per page</label>
                                    <select class="custom-select" v-model="pageSize" @change="handleChangePageSize()">
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                    </select>
                                </div>

                                <div>
                                    <b-pagination
                                        v-model="currentPage"
                                        :current-page="currentPage"
                                        :per-page="results.pageSize"
                                        :total-rows="results.totalCount"
                                        prev-text="< Previous"
                                        next-text="Next >"
                                        @change="pageChange"
                                        aria-controls="table"
                                        hide-goto-end-buttons
                                        prev-class="prev-page"
                                        next-class="next-page"
                                        align="right"
                                        class="mb-0"
                                    ></b-pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapActions, mapGetters} from 'vuex'
import Loader from '../../../Common/components/Loader.vue'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import PatientSearchbar from '@/components/PatientSearchbarComponent.vue'
import TotalResultsMixin from '../../../Common/mixins/TotalResultsMixin'
import FormatterMixin from '../../../Common/mixins/FormatterMixin'
import FulfilmentOptions from '../../../Common/enums/FulfilmentOptionsType'
import IssueMethod from '../../../Common/enums/IssueMethod'
import SignalRMixin from '@/mixins/SignalRMixin'
export default {
    name: 'SelectAPatient',

    components: {
        Loader,
        Breadcrumbs,
        PatientSearchbar
    },

    mixins: [
        TotalResultsMixin,
        FormatterMixin,
        SignalRMixin
    ],

    data() {
        return {
            loading: true,
            showClearResults: false,
            isBusy: false,
            currentPage: 1,
            pageSize: process.env.VUE_APP_PAGINATION_PAGE_SIZE,
            offset: 0,
            sortColumn: 'PATIENT_NAME',
            sortType: 'DESCENDING',
            sortDesc: true,
            sortBySync: 'fullName',
            results: {},
            issueInPersonOverride: process.env.VUE_APP_ISSUE_IN_PERSON_OVERRIDE == 1 ? true : false,
            FulfilmentOptions,
            IssueMethod
        }
    },

    mounted() {
        this.clear()
    }, 

    computed: {
        ...mapGetters({
            organisationId: 'UserStore/GetUserOrganisationId',
            selectAPatientText: 'SearchStore/GetSelectAPatientText',
            patientIdentifierLabel: 'OrganisationStore/GetPatientIdentifierLabel',
            facilitiesAllDisabled: 'FacilityStore/GetAllFacilitiesDisabled',
            myOrganisationSettings: 'OrganisationStore/GetMyOrganisationSettings'
        }),

        filteredPatientsFields() {
            return [
                {
                    key: 'identifier',
                    label: this.patientIdentifierLabel,
                    sortable: true,
                },
                {
                    key: 'fullName',
                    label: 'Patient name',
                    variant: 'text-wrap w-15',
                    sortable: true,
                },
                {
                    key: 'dateOfBirth',
                    label: 'DOB',
                    sortable: true,
                    formatter: 'formatDate'
                },
                {
                    key: 'address',
                    label: 'Address',
                    variant: 'text-wrap',
                    formatter: 'formatShippingAddress'
                },
                {
                    key: 'previousTests',
                    sortable: true,
                    label: 'Previous Tests'
                },
                {
                    key: 'action',
                    label: ''
                }
            ]
        }
    },

    methods: {
        ...mapActions({
            setPatientDetailsInTestStoreAction: 'TestStore/SetPatientDetails',
            clearTestStore: 'TestStore/ClearData',
            setNotification: 'NotificationStore/SetNotification',
            setSelectAPatientSearchbarDataInSearchStore: 'SearchStore/SetSelectAPatientSearchbarData',
            setShippingAddressTypeInTestStoreAction: 'TestStore/SetShippingAddressType',
            setDeliveryMethodInTestStoreAction: 'TestStore/SetDeliveryMethod'
        }),

        async getPatients() {
            this.isBusy = true
            
            try {
                if(this.selectAPatientText != null) {
                    let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}/${this.selectAPatientText}/${this.pageSize}/${this.offset}/${this.sortColumn}/${this.sortType}`)

                    this.results = response.data
                } else {
                    let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}/${this.pageSize}/${this.offset}/${this.sortColumn}/${this.sortType}`)

                    this.results = response.data
                }
             }
             catch(err) {
                 this.setNotification({
                     type: 'error',
                     title: 'Error',
                     message: 'Something went wrong. The patients were not fetched'
                 })
             }
             finally {
                 this.loading = false
                 this.isBusy = false
             }
        },

        updateResults(data) {
            this.currentPage = 1
            this.offset = 0
            this.results = data
            this.showClearResults = true
        },

        clear() {
            this.currentPage = 1
            this.offset = 0
            this.setSelectAPatientSearchbarDataInSearchStore({
                patientText: null
            })
            this.getPatients()
        },

        async assignTestKitToPatient(data) {
            this.clearTestStore()

            this.setPatientDetailsInTestStoreAction(data)

            if(this.myOrganisationSettings.fulfilmentOptions == this.FulfilmentOptions.POST) {
                await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.POST)
                await this.setShippingAddressTypeInTestStoreAction(0)
                return this.$router.push({name: 'TestDetails'})
            }

            if(this.myOrganisationSettings.fulfilmentOptions == this.FulfilmentOptions.IN_PERSON) {
                await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.IN_PERSON)
                await this.setShippingAddressTypeInTestStoreAction(0)
                return this.$router.push({name: 'TestDetails'})
            }
            
            if(this.myOrganisationSettings.fulfilmentOptions == this.FulfilmentOptions.ALL) {
                return this.$router.push({name: 'DeliveryMethod'})
            }

            if(this.issueInPersonOverride) {
                await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.IN_PERSON)
                await this.setShippingAddressTypeInTestStoreAction(0)
                return this.$router.push({name: 'TestDetails'})
            }

            await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.POST)
            await this.setShippingAddressTypeInTestStoreAction(0)
            return this.$router.push({name: 'TestDetails'})
        },

        pageChange(value) {
            this.currentPage = value
            this.offset = (value - 1) * this.pageSize
            this.getPatients()
        },

        sortingChanged(e) {
            switch(e.sortBy) {
                case 'fullName':
                    this.sortColumn = 'PATIENT_NAME' 
                break;

                case 'identifier':
                    this.sortColumn = 'PATIENT_IDENTIFIER'
                break;

                case 'dateOfBirth':
                    this.sortColumn = 'PATIENT_DOB'
                break;

                case 'previousTests':
                    this.sortColumn = 'PREVIOUS_TESTS'
                break;
            }

            this.sortDesc = e.sortDesc

            if(this.sortDesc) {
                this.sortType = 'DESCENDING'
            } else {
                this.sortType = 'ASCENDING'
            }

            this.getPatients()
        },

        handleChangePageSize () {
            this.currentPage = 1
            this.offset = 0

            this.getPatients()
        }
    }
}
</script>